import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { AllRichContentSupportedTypes, RichContentSupportedTypes } from '../../../../models';
import {
	CompanyViewModel,
	ExtendedSupportedTimelineTypes,
	RichContentViewModel,
	TimelineEventTypes,
	UserSessionContext,
} from '../../../../viewmodels/AppViewModels';
import { CheckboxFilterDropdown, ICheckboxFilterOption } from '../../CheckboxFilterDropdown';
import { EntityDataBoards, anyDataBoardEnabledForCompany } from '../../dataBoards/EntityDataBoards';
import { EntityRichContent } from '../../entities/EntityRichContent';
import { CompanyTimeline } from '../../entities/EntityTimeline/CompanyTimeline';
import { ITimelineFilterOption, TimelineFilter } from '../../entities/EntityTimeline/TimelineFilter';
import { DataboardsIcon } from '../../svgs/icons/DataboardsIcon';
import { NotesIcon } from '../../svgs/icons/NotesIcon';
import { PeopleIcon } from '../../svgs/icons/PeopleIcon';
import { TabContent, TabList, TabRoot, TabTrigger } from '../../Tabs';
import { CompanyContactsList } from '../CompanyContactsList';
import { styleSheet } from './styles';

enum CompanyDetailTabs {
	History = 'history',
	Notes = 'notes',
	Contacts = 'contacts',
	DataBoards = 'dataBoards',
}

interface IProps {
	company: CompanyViewModel;
	userSession?: UserSessionContext;
}

interface IState {
	richContentSupportedTypes?: RichContentSupportedTypes[];
	timelineEventTypes?: TimelineEventTypes[];
	timelineFilterIds?: string[];
}

const richContentStorageKey = 'notesActionsFilterOptions';
const timelineStorageKey = 'companyTimelineFilterOptions';

// Each option id is the rich content type it filters on, so the committed selection is also what gets
// persisted — keeping the stored format identical to the filter this replaced.
const RICH_CONTENT_FILTER_OPTIONS: ICheckboxFilterOption[] = [
	{ id: 'Note', name: 'notes', label: 'Show notes' },
	{ id: 'ActionItem', name: 'action items', label: 'Show action items' },
	{ id: 'EmailNote', name: 'emails', label: 'Show emails' },
];

// `filter(Boolean)` drops the empty segment a blank stored value splits into: passing '' as a type
// would otherwise reach the API, and an empty list makes the server return every type.
const readStoredValues = (key: string) => window.localStorage.getItem(key)?.split('|').filter(Boolean);

const getInitialRichContentTypes = (): RichContentSupportedTypes[] => {
	const storedTypes = readStoredValues(richContentStorageKey);
	return !storedTypes?.length || storedTypes.includes('All')
		? (AllRichContentSupportedTypes as RichContentSupportedTypes[])
		: (storedTypes.filter(type => type !== 'All') as RichContentSupportedTypes[]);
};

class _CompanyActivity extends React.Component<IProps, IState> {
	public readonly state: IState = {
		richContentSupportedTypes: getInitialRichContentTypes(),
		timelineEventTypes: this.getTimelineSupportedTypes(),
		timelineFilterIds: this.getSelectedTimelineFilterIds(),
	};

	public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
		if (
			this.props.company !== nextProps.company &&
			!!this.props.company &&
			!!nextProps.company &&
			this.props.company.id !== nextProps.company.id
		) {
			nextProps.company.getRichContent();
		}
	}

	public render() {
		const { company, userSession } = this.props;
		const showTimeline = !!userSession.account.features?.timeline?.enabled;
		const { richContentSupportedTypes, timelineEventTypes, timelineFilterIds } = this.state;
		const showDataBoards = anyDataBoardEnabledForCompany(userSession);

		return (
			<TabRoot
				defaultValue={showTimeline ? CompanyDetailTabs.History : CompanyDetailTabs.Notes}
				className={css(styleSheet.tabRoot)}
			>
				<TabList styles={[styleSheet.tabList]}>
					<TabTrigger
						variant='none'
						styles={[styleSheet.tabTrigger]}
						value={showTimeline ? CompanyDetailTabs.History : CompanyDetailTabs.Notes}
					>
						<span className={css(styleSheet.tabIcon)}>
							<NotesIcon fillColor='currentColor' />
						</span>
						<span className={css(styleSheet.tabTitle)}>{showTimeline ? 'History' : 'Notes / Actions'}</span>
					</TabTrigger>
					<TabTrigger variant='none' styles={[styleSheet.tabTrigger]} value={CompanyDetailTabs.Contacts}>
						<span className={css(styleSheet.tabIcon)}>
							<PeopleIcon />
						</span>
						<span className={css(styleSheet.tabTitle)}>Contacts</span>
					</TabTrigger>
					{showDataBoards ? (
						<TabTrigger variant='none' styles={[styleSheet.tabTrigger]} value={CompanyDetailTabs.DataBoards}>
							<span className={css(styleSheet.tabIcon)}>
								<DataboardsIcon fill='currentColor' />
							</span>
							<span className={css(styleSheet.tabTitle)}>Data Boards</span>
						</TabTrigger>
					) : null}
				</TabList>
				{showTimeline ? (
					<TabContent value={CompanyDetailTabs.History}>
						<div className={css(styleSheet.timelineFilterOptionsContainer)}>
							<TimelineFilter
								options={this.getTimelineFilterOptions()}
								selectedIds={timelineFilterIds ?? []}
								onCommit={this.onTimelineFilterCommit}
							/>
						</div>
						<CompanyTimeline
							companyId={company.id}
							onDeleteRichContent={(richContent: RichContentViewModel) => company.deleteRichContent(richContent)}
							eventTypes={timelineEventTypes}
						/>
					</TabContent>
				) : (
					<TabContent value={CompanyDetailTabs.Notes}>
						<div className={css(styleSheet.richContentFilterOptionsContainer)}>
							<CheckboxFilterDropdown
								ariaLabel='Filter notes and actions by type'
								onCommit={this.onRichContentFilterCommit}
								options={RICH_CONTENT_FILTER_OPTIONS}
								selectedIds={richContentSupportedTypes ?? []}
							/>
						</div>
						<EntityRichContent entity={company} supportedTypes={richContentSupportedTypes} />
					</TabContent>
				)}
				<TabContent value={CompanyDetailTabs.Contacts}>
					<CompanyContactsList company={company} />
				</TabContent>
				{showDataBoards ? (
					<TabContent value={CompanyDetailTabs.DataBoards}>
						<React.Suspense fallback={<></>}>
							<EntityDataBoards entityViewModel={company} />
						</React.Suspense>
					</TabContent>
				) : null}
			</TabRoot>
		);
	}

	private getSelectedTimelineFilterIds(): string[] {
		const storedTypeGroups = readStoredValues(timelineStorageKey);
		const options = this.getTimelineFilterOptions();

		if (!storedTypeGroups?.length) {
			return options.map(option => option.id);
		}

		const selectedIds = options
			.filter(option => storedTypeGroups.includes(option.types.join(',')))
			.map(option => option.id);
		// Matches the timeline's own empty-means-all fallback, so the checkboxes can never disagree with it.
		return selectedIds.length > 0 ? selectedIds : options.map(option => option.id);
	}

	private getTimelineFilterOptions(): ITimelineFilterOption[] {
		const { userSession } = this.props;

		const isCoffeeUser = userSession.account.features.aida.enabled;

		return [
			{ id: 'notes', name: 'notes', label: 'Show notes', types: ['NoteEvent'] },
			{ id: 'action-items', name: 'action items', label: 'Show action items', types: ['ActionItemEvent'] },
			{ id: 'deals', name: 'deals', label: 'Show deals', types: ['DealCreatedEvent', 'DealUpdatedEvent'] },
			{ id: 'emails', name: 'emails', label: 'Show emails', types: ['SentEmailEvent'] },
			{ id: 'texts', name: 'conversations', label: 'Show texts', types: ['ConversationThreadEvent'] },
			{ id: 'meetings', name: 'meetings', label: 'Show meetings', types: ['MeetingEvent'] },
			{
				id: 'phone-calls',
				name: 'phone calls',
				label: 'Show phone calls',
				types: isCoffeeUser
					? ['PhoneCallEvent', 'PhoneCallCompletedEvent', 'UntrackedPhoneCallEvent']
					: ['PhoneCallEvent'],
			},
		];
	}

	private getTimelineSupportedTypes(): TimelineEventTypes[] {
		const storedTypeGroups = readStoredValues(timelineStorageKey);
		if (!storedTypeGroups?.length || storedTypeGroups.includes('All')) {
			return ExtendedSupportedTimelineTypes as TimelineEventTypes[];
		}
		return storedTypeGroups.flatMap(group => group.split(',')) as TimelineEventTypes[];
	}

	private onRichContentFilterCommit = (selectedIds: string[]) => {
		window.localStorage.setItem(richContentStorageKey, selectedIds.join('|'));
		this.setState({ richContentSupportedTypes: selectedIds as RichContentSupportedTypes[] });
	};

	private onTimelineFilterCommit = (selectedIds: string[]) => {
		const selected = this.getTimelineFilterOptions()
			.filter(option => selectedIds.includes(option.id))
			.map(option => option.types.join(','));

		try {
			window.localStorage.setItem(timelineStorageKey, selected.join('|'));
		} catch {
			// Storage unavailable (private mode / quota) — the filter still applies for this session.
		}
		this.setState({ timelineFilterIds: selectedIds, timelineEventTypes: this.getTimelineSupportedTypes() });
	};
}

export const CompanyActivity = observer(_CompanyActivity);
